
import React, { useState } from 'react';
import { Instagram, Linkedin, Twitter, Facebook, MessageCircle } from 'lucide-react';
import NavbarLogo from './nav/NavbarLogo';
import LegalModal from './LegalModal';

const Footer = () => {
  const [isTermsOpen, setIsTermsOpen] = useState(false);
  const [isPrivacyOpen, setIsPrivacyOpen] = useState(false);
  
  const currentYear = new Date().getFullYear();
  const currentUrl = window.location.href;
  
  const shareLinks = [
    {
      name: 'Instagram',
      icon: Instagram,
      url: `https://www.instagram.com/share?url=${encodeURIComponent(currentUrl)}`,
      color: 'hover:bg-pink-600'
    },
    {
      name: 'LinkedIn',
      icon: Linkedin,
      url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(currentUrl)}`,
      color: 'hover:bg-blue-600'
    },
    {
      name: 'Twitter',
      icon: Twitter,
      url: `https://twitter.com/intent/tweet?url=${encodeURIComponent(currentUrl)}&text=Check out this Free Pomodoro Timer!`,
      color: 'hover:bg-blue-400'
    },
    {
      name: 'Facebook',
      icon: Facebook,
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(currentUrl)}`,
      color: 'hover:bg-blue-700'
    },
    {
      name: 'WhatsApp',
      icon: MessageCircle,
      url: `https://wa.me/?text=Check out this Free Pomodoro Timer! ${encodeURIComponent(currentUrl)}`,
      color: 'hover:bg-green-600'
    }
  ];

  const handleShare = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      <footer className="w-full bg-gray-100 dark:bg-gray-950 border-t border-gray-200 dark:border-gray-800" role="contentinfo">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="py-12 lg:py-16">
            {/* Main Footer Content - 4 Column Layout */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
              
              {/* Column 1: Brand & Description */}
              <div className="space-y-4">
                <a href="/" className="inline-block" aria-label="Free Pomodoro Timer - Go to homepage">
                  <NavbarLogo isLightBackground={true} />
                </a>
                <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                  A free Pomodoro timer designed to help you focus and boost productivity with the proven Pomodoro Technique.
                </p>
              </div>

              {/* Column 2: Navigation */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider">
                  Features
                </h3>
                <nav aria-label="Footer navigation">
                  <ul className="space-y-3">
                    <li>
                      <a 
                        href="/" 
                        className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors duration-200"
                      >
                        Overview
                      </a>
                    </li>
                    <li>
                      <a 
                        href="/pomodoro" 
                        className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors duration-200"
                      >
                        Pomodoro Timer
                      </a>
                    </li>
                    <li>
                      <a 
                        href="/kanban" 
                        className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors duration-200"
                      >
                        Kanban Board
                      </a>
                    </li>
                    <li>
                      <a 
                        href="/eisenhower" 
                        className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors duration-200"
                      >
                        Eisenhower Matrix
                      </a>
                    </li>
                  </ul>
                </nav>
              </div>

              {/* Column 3: Related Tools */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider">
                  Related Tools
                </h3>
                <ul className="space-y-3">
                  <li>
                    <a 
                      href="https://www.thepokertimer.com" 
                      target="_blank" 
                      rel="follow noopener noreferrer"
                      className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors duration-200"
                    >
                      The Poker Timer
                    </a>
                  </li>
                  <li>
                    <a 
                      href="https://freepokerclock.com/" 
                      target="_blank" 
                      rel="follow noopener noreferrer"
                      className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors duration-200"
                    >
                      Free Poker Clock
                    </a>
                  </li>
                </ul>
              </div>

              {/* Column 4: Share & Connect */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider">
                  Share This Site
                </h3>
                <div className="flex flex-wrap gap-3">
                  {shareLinks.map((share) => {
                    const IconComponent = share.icon;
                    return (
                      <button
                        key={share.name}
                        onClick={() => handleShare(share.url)}
                        className={`w-10 h-10 rounded-lg bg-gray-200 dark:bg-gray-700 flex justify-center items-center transition-all duration-200 ${share.color} hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 dark:focus:ring-gray-400`}
                        aria-label={`Share on ${share.name}`}
                        title={`Share on ${share.name}`}
                      >
                        <IconComponent size={18} className="text-gray-700 dark:text-gray-300" />
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-500">
                  Help others discover this free productivity tool
                </p>
              </div>
            </div>
          </div>
          
          {/* Bottom Section */}
          <div className="border-t border-gray-200 dark:border-gray-700 py-6">
            <div className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0">
              
              {/* Legal Links */}
              <div className="flex items-center space-x-6">
                <button 
                  onClick={() => setIsTermsOpen(true)}
                  className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 dark:focus:ring-gray-400 rounded-sm"
                  aria-label="View Terms and Conditions"
                >
                  Terms & Conditions
                </button>
                <button 
                  onClick={() => setIsPrivacyOpen(true)}
                  className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 dark:focus:ring-gray-400 rounded-sm"
                  aria-label="View Privacy Policy"
                >
                  Privacy Policy
                </button>
              </div>
              
              {/* Copyright */}
              <div className="text-sm text-gray-500 dark:text-gray-500">
                © {currentYear} Free Pomodoro Timer. All rights reserved.
              </div>
            </div>
          </div>
        </div>
      </footer>
      
      {/* Legal Modals */}
      <LegalModal 
        isOpen={isTermsOpen} 
        onClose={() => setIsTermsOpen(false)} 
        type="terms" 
      />
      <LegalModal 
        isOpen={isPrivacyOpen} 
        onClose={() => setIsPrivacyOpen(false)} 
        type="privacy" 
      />
    </>
  );
};

export default Footer;
